import { silentPrint, desktop, getPrinters } from "./desktopPrint";
const silentReady = () => !!desktop() && !!getPrinters().bill_printer;
import { useState } from "react";
import { Printer, FileSpreadsheet, Loader2 } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { fmtD, fmtDT } from "./units";

const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const cell = (c, v) => (v === null || v === undefined || v === "" ? "" : c.type === "money" ? money(v) : c.type === "date" ? fmtD(v) : c.type === "pct" ? `${v}%` : c.type === "number" ? Number(v).toLocaleString("en-IN") : esc(v));

export const PAPER_KEY = "fd_paper_width";
export const getPaper = () => localStorage.getItem(PAPER_KEY) || "80";
export const setPaper = (w) => localStorage.setItem(PAPER_KEY, w);

export function openDoc(title, css, body, win) {
  const w = win || window.open("", "_blank", "width=900,height=700");
  if (!w) { alert("Allow pop-ups to print"); return; }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>table{font-size:inherit}${css}</style></head><body>${body}<script>window.onload=()=>{setTimeout(()=>{window.print()},250)}</script></body></html>`);
  w.document.close();
}

const A4_CSS = (landscape) => `
  @page{size:A4 ${landscape ? "landscape" : "portrait"};margin:16mm 14mm 18mm;@bottom-right{content:"Page " counter(page) " of " counter(pages);font:9px Arial;color:#555}}
  body{font:10.5px/1.45 Arial,Helvetica,sans-serif;color:#111;margin:0;padding:2mm 3mm}
  .hd{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #111;padding:2px 4px 10px;margin-bottom:14px}
  .hd h1{font-size:15px;margin:0 0 3px}.hd small{display:block;color:#444;font-size:10px}
  table{width:100%;border-collapse:collapse;table-layout:auto}
  thead{display:table-header-group}tfoot{display:table-row-group}tr{page-break-inside:avoid}
  th{background:#eee;text-align:left;font-weight:600;border:1px solid #bbb;padding:7px 9px;font-size:10px}
  td{border:1px solid #ccc;padding:6px 9px;vertical-align:top;word-break:break-word}
  td.n,th.n{text-align:right;white-space:nowrap}
  tr.tot td{font-weight:700;background:#f5f5f5}
  .sec{margin-top:16px}.note{color:#555;font-size:9.5px;margin:10px 4px 0}
`;

export function tableHtml({ columns, rows, totals }) {
  const num = (c) => ["money", "number", "pct"].includes(c.type);
  return `<table><thead><tr>${columns.map((c) => `<th class="${num(c) ? "n" : ""}">${esc(c.label)}</th>`).join("")}</tr></thead><tbody>
    ${rows.map((r) => `<tr${r._bold ? ' class="tot"' : ""}>${columns.map((c) => `<td class="${num(c) ? "n" : ""}">${cell(c, r[c.key])}</td>`).join("")}</tr>`).join("") || `<tr><td colspan="${columns.length}">No records</td></tr>`}
    ${totals ? `<tr class="tot">${columns.map((c) => `<td class="${num(c) ? "n" : ""}">${c.key in totals ? cell(c, totals[c.key]) : ""}</td>`).join("")}</tr>` : ""}
  </tbody></table>`;
}

export function printA4(doc) {
  const head = `<div class="hd"><div><h1>${esc(doc.title)}</h1><small>${esc(doc.outlet || "")}${doc.outletInfo ? " · " + esc(doc.outletInfo) : ""}</small></div><div><small>${esc(doc.period || "")}</small><small>Printed ${fmtDT(new Date().toISOString())} IST</small></div></div>`;
  const sections = (doc.sections || [doc]).map((s) => `<div class="sec">${s.heading ? `<h3 style="margin:4px 0;font-size:12px">${esc(s.heading)}</h3>` : ""}${tableHtml(s)}</div>`).join("");
  openDoc(doc.title, A4_CSS(doc.landscape), head + (doc.preHtml || "") + sections + (doc.notes || []).map((n) => `<p class="note">${esc(n)}</p>`).join(""));
}

export async function exportXlsx(doc) {
  const sections = (doc.sections || [doc]).map((s) => ({ heading: s.heading || "", columns: s.columns, rows: s.rows, totals: s.totals || null }));
  const res = await api.post(`/export/xlsx`, { title: doc.title, subtitle: [doc.outlet, doc.period].filter(Boolean).join(" · "), sections, filename: doc.filename || doc.title }, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `${(doc.filename || doc.title).replace(/[^A-Za-z0-9_-]+/g, "_")}.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function ExportBar({ getDoc, testid = "export" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const xl = async () => { setBusy(true); setErr(""); try { await exportXlsx(getDoc()); } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); } };
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }} className="no-print">
      <button type="button" className="secondary-button" onClick={() => printA4(getDoc())} data-testid={`${testid}-print`}><Printer size={14} /> Print / PDF</button>
      <button type="button" className="secondary-button" disabled={busy} onClick={xl} data-testid={`${testid}-excel`}>{busy ? <Loader2 className="spin" size={14} /> : <FileSpreadsheet size={14} />} Excel</button>
      {err && <small className="orange-text" data-testid={`${testid}-error`}>{err}</small>}
    </span>
  );
}

export function PaperSelect({ testid = "paper-width" }) {
  const [w, setW] = useState(getPaper());
  return (
    <select value={w} onChange={(e) => { setPaper(e.target.value); setW(e.target.value); }} data-testid={testid} title="Printer paper on this device" className="no-print">
      <option value="58">Thermal 58 mm</option><option value="80">Thermal 80 mm</option><option value="a4">A4</option>
    </select>
  );
}

const THERMAL_CSS = (mm) => `
  @page{size:${mm}mm auto;margin:0}
  body{width:${mm - 8}mm;margin:0 auto;padding:4mm 1mm 6mm;font:${mm === 58 ? 10 : 11.5}px/1.4 "Courier New",monospace;color:#000}
  h2{font-size:${mm === 58 ? 12 : 14}px;text-align:center;margin:0 0 4px}
  .c{text-align:center}.r{text-align:right}.b{font-weight:700}
  hr{border:0;border-top:1px dashed #000;margin:5px 0}
  table{width:100%;border-collapse:collapse}td{padding:2px 1px;vertical-align:top}
  .big{font-size:${mm === 58 ? 13 : 16}px;font-weight:700}
`;

export function printThermalOrA4(title, thermalBody, a4Doc) {
  const p = getPaper();
  if (p === "a4") return a4Doc ? printA4(a4Doc) : openDoc(title, A4_CSS(false) + "body{font-size:12px}", thermalBody);
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>table{font-size:inherit}${THERMAL_CSS(Number(p))}</style></head><body>${thermalBody}</body></html>`;
  const w = silentReady() ? null : window.open("", "_blank", "width=900,height=700");
  if (!w) return silentPrint("bill", html);
  openDoc(title, THERMAL_CSS(Number(p)), thermalBody, w);
}

const modeLabel = (m) => (m === "credit" ? "CREDIT (due)" : String(m).toUpperCase());
function payRows(bill, row) {
  if (!bill.payments) return bill.payment_mode ? row("Paid by", esc(modeLabel(bill.payment_mode))) : "";
  const paid = bill.payments.map((p) => row(`Paid ${esc(modeLabel(p.mode))}`, money(p.amount))).join("");
  return bill.payment_status === "paid" ? paid : `${paid}${row(bill.payments.length ? "BALANCE DUE" : "UNPAID · AMOUNT DUE", money(bill.balance_due ?? bill.grand_total), "b")}`;
}

export function billThermal(bill, outlet) {
  const o = bill.outlet || outlet || {};
  const it = (bill.items || []).map((i) => `<tr><td colspan="3">${esc(i.name)}</td></tr><tr><td>${i.qty} x ${money(i.unit_price)}</td><td class="r">${i.gst_rate ?? 5}%</td><td class="r">${money(i.line_amount ?? i.qty * i.unit_price)}</td></tr>`).join("");
  const row = (l, v, cls = "") => `<tr class="${cls}"><td>${l}</td><td class="r">${v}</td></tr>`;
  return `<h2>${esc(o.display_name || o.name || "FynDine")}</h2>
    <div class="c">${esc(o.address || o.city || "")}${o.gstin ? `<br>GSTIN ${esc(o.gstin)}` : ""}${o.fssai ? `<br>FSSAI ${esc(o.fssai)}` : ""}</div><hr>
    <div class="c b">TAX INVOICE${bill.status === "cancelled" ? " — CANCELLED" : ""}</div>
    <table>${row("Invoice", esc(bill.invoice_no))}${row("Date", fmtDT(bill.created_at))}
      ${bill.table_label ? row("Table", esc(bill.table_label)) : ""}${bill.waiter_name ? row("Waiter", esc(bill.waiter_name)) : ""}${bill.customer_name ? row("Customer", esc(bill.customer_name)) : ""}
      ${(bill.kot_numbers || []).length ? row("KOT", esc(bill.kot_numbers.join(", "))) : ""}${payRows(bill, row)}</table><hr>
    <table>${it}</table><hr>
    <table>${row("Subtotal", money(bill.subtotal))}${bill.igst ? row("IGST", money(bill.igst)) : row("CGST", money(bill.cgst)) + row("SGST", money(bill.sgst))}
      ${bill.service_charge ? row("Service charge", money(bill.service_charge)) : ""}${bill.discount ? row("Discount", "-" + money(bill.discount)) : ""}
      ${bill.loyalty_redeemed ? row("Points redeemed", "-" + money(bill.loyalty_redeemed)) : ""}${row("TOTAL", money(bill.grand_total), "big")}</table><hr>
    ${bill.room_charge ? `<div class="c b">Charged to Room ${esc(bill.room_charge.room_number)} (${esc(bill.room_charge.booking_ref)})</div><hr>` : ""}
    <div class="c">Thank you! Visit again</div>`;
}

export function kotThermal(kot, qty, sub) {
  return `<h2>${kot.type === "modification" ? "** MODIFIED KOT **<br>" : ""}${esc(kot.kot_no)}</h2><hr>
    ${kot.ref_kot_no ? `<div>Ref ${esc(kot.ref_kot_no)} · ${esc(kot.modified_by || "")}</div>` : ""}
    <div class="b">Table: ${esc(kot.table_label)}</div>${kot.waiter_name ? `<div>Waiter: ${esc(kot.waiter_name)}</div>` : ""}
    <div>${fmtDT(kot.printed_at)}</div><hr>
    <table>${kot.items.map((i) => `<tr><td class="big" style="width:18%">${qty(i)}x</td><td class="big">${esc(i.name)}${sub(i) ? `<br><small style="font-weight:400">${esc(sub(i))}</small>` : ""}</td></tr>`).join("")}</table><hr>
    <div class="c">— KITCHEN COPY —</div>`;
}

export function printBill(bill, a4) {
  if (getPaper() === "a4") return a4 ? a4() : window.open(`/invoice/${bill.id}`, "_blank");
  printThermalOrA4(bill.invoice_no, billThermal(bill));
}
